import { useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useSession } from "@jkast90/kast-common/auth";
import { queryKeys } from "../library/queries";
import { ejsLocalSaves, mgbaLocalSaves } from "../library/localSaves";
import { cloudSaves, type CloudSaveMeta } from "./cloudSaves";
import { ejsRoms } from "./ejs";
import { isSaveFileFor, saveFileName } from "./saves";
import { checksum, planSync, type SyncRecords } from "./syncPlan";
import { loadRecords, ownsLocalSaves, saveRecords, syncedRoms } from "./syncRecords";

// A game stopped or a tab closed while offline leaves saves the cloud
// hasn't seen. useCloudSync only runs for the open game, so this uploads
// every synced game's outstanding files straight from browser storage once
// the browser is back online: on load, on the online event and when the
// tab is shown. Only uploads: a cloud copy from another device, or a
// conflict, is settled when the game is opened.

let openRom: string | null = null;

/** The game useCloudSync is looking after, which this leaves alone. */
export function setSyncingRom(rom: string | null) {
  openRom = rom;
}

interface Pending {
  rom: string;
  records: SyncRecords;
  files: { name: string; sum: number; bytes: Uint8Array }[];
}

async function localFiles(roms: string[]): Promise<Pending[]> {
  const ejsGames = await ejsRoms.list().catch(() => [] as string[]);
  const [mgba, ejs] = await Promise.all([
    mgbaLocalSaves(),
    ejsLocalSaves(roms.filter((rom) => ejsGames.includes(rom))),
  ]);
  return roms.map((rom) => {
    const own = mgba.filter((f) => isSaveFileFor(rom, f.name)).map((f) => ({ name: f.name, bytes: f.bytes }));
    const save = ejs.get(rom);
    if (save) own.push({ name: saveFileName(rom), bytes: save.bytes });
    return { rom, records: loadRecords(rom), files: own.map((f) => ({ ...f, sum: checksum(f.bytes) })) };
  });
}

/** True when something went up. */
async function run(): Promise<boolean> {
  if (!navigator.onLine || !ownsLocalSaves()) return false;
  const roms = syncedRoms().filter((rom) => rom !== openRom);
  if (!roms.length) return false;
  const pending = await localFiles(roms);
  const cloud = await cloudSaves.list();
  const uploaded: { rom: string; name: string; sum: number }[] = [];
  for (const { rom, records, files } of pending) {
    const plan = planSync(
      files,
      cloud.filter((c) => isSaveFileFor(rom, c.name)),
      records,
    );
    for (const name of plan.push) {
      const file = files.find((f) => f.name === name)!;
      await cloudSaves.upload(name, file.bytes);
      uploaded.push({ rom, name, sum: file.sum });
    }
  }
  if (!uploaded.length) return false;
  const after: CloudSaveMeta[] = await cloudSaves.list();
  for (const { rom, name, sum } of uploaded) {
    const meta = after.find((c) => c.name === name);
    if (!meta) continue;
    const records = loadRecords(rom);
    records[name] = { sum, at: meta.updatedAt };
    saveRecords(rom, records);
  }
  return true;
}

let running: Promise<boolean> | null = null;

/** Uploads what the cloud is missing; one pass at a time, failures wait for the next trigger. True when something went up. */
export function uploadPendingSaves(): Promise<boolean> {
  running ??= run()
    .catch(() => false)
    .finally(() => {
      running = null;
    });
  return running;
}

/** Runs a pass on load, on sign-in, when the browser comes back online and when the tab is shown. */
export function useOfflineSync() {
  const signedIn = useSession().status === "signedIn";
  const queryClient = useQueryClient();
  useEffect(() => {
    if (!signedIn) return;
    // The library page lists the cloud's saves from the query cache.
    const tick = () => {
      void uploadPendingSaves().then((uploaded) => {
        if (uploaded) void queryClient.invalidateQueries({ queryKey: queryKeys.cloudSaves });
      });
    };
    tick();
    const onVisible = () => !document.hidden && tick();
    window.addEventListener("online", tick);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      window.removeEventListener("online", tick);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [signedIn, queryClient]);
}
